import React, { useEffect } from 'react';
import { X, Scale } from 'lucide-react';
import { SITE_CONFIG } from '../config/site';

export type LegalDocType = 'terms' | 'privacy' | 'ai' | null;

interface LegalModalProps {
  type: LegalDocType;
  onClose: () => void;
}

export const LegalModal: React.FC<LegalModalProps> = ({ type, onClose }) => {
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && type) {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [type, onClose]);

  if (!type) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-brand-bg/85 backdrop-blur-md animate-in fade-in duration-200">
      <div
        className="w-full max-w-2xl max-h-[85vh] rounded-3xl bg-brand-surface border border-brand-border p-6 sm:p-8 shadow-2xl relative flex flex-col overflow-hidden"
        role="dialog"
        aria-modal="true"
      >
        {/* Header */}
        <div className="flex items-center justify-between pb-4 border-b border-brand-border mb-4 shrink-0">
          <div className="flex items-center gap-2">
            <Scale className="w-5 h-5 text-brand-cyan" />
            <h3 className="text-lg sm:text-xl font-bold text-white">
              {type === 'terms' && 'Termos de Uso da Plataforma'}
              {type === 'privacy' && 'Política de Privacidade e Proteção de Dados (LGPD)'}
              {type === 'ai' && 'Declaração Institucional de Uso de Inteligência Artificial'}
            </h3>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-xl text-brand-textSubtle hover:text-white hover:bg-white/5 transition-colors"
            aria-label="Fechar janela"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Scrollable Content */}
        <div className="overflow-y-auto pr-2 space-y-4 text-xs sm:text-sm text-brand-textMuted leading-relaxed">
          <div className="p-3 rounded-xl bg-brand-bg border border-brand-border text-[11px] font-mono text-brand-textSubtle">
            STATUS: MINUTA INSTITUCIONAL PRE-BETA · REVISÃO 2026.1 · DB8 INTELLIGENCE
          </div>

          {type === 'terms' && (
            <>
              <h4 className="text-white font-bold text-sm">1. Objeto e Natureza do Serviço</h4>
              <p>
                O InfoForge é uma plataforma de software como serviço (SaaS) assistida por inteligência artificial, destinada à estruturação, compilação técnica e diagramação de infoprodutos digitais (ebooks, cadernos de exercícios e roteiros comerciais).
              </p>
              <h4 className="text-white font-bold text-sm">2. Propriedade Intelectual do Usuário</h4>
              <p>
                O usuário mantém a titularidade integral e irrestrita sobre todos os conteúdos, marcas, ideias e produtos gerados em sua conta. A DB8 Intelligence não reivindica direitos patrimoniais ou royalties sobre as obras criadas pelo usuário.
              </p>
              <h4 className="text-white font-bold text-sm">3. Responsabilidade Editorial</h4>
              <p>
                Cabe exclusivamente ao usuário a verificação final, revisão conceitual e adequação regulatória dos produtos finais antes de qualquer disponibilização comercial ou jurídica ao público.
              </p>
              <h4 className="text-white font-bold text-sm">4. Condições do Private Beta</h4>
              <p>
                O acesso durante a fase de Private Beta é outorgado em caráter experimental, podendo ter limites de taxa, janelas de manutenção e cotas de processamento ajustadas para garantir a estabilidade operacional do ambiente compartilhado.
              </p>
            </>
          )}

          {type === 'privacy' && (
            <>
              <h4 className="text-white font-bold text-sm">1. Compromisso LGPD (Lei nº 13.709/2018)</h4>
              <p>
                A DB8 Intelligence coleta e trata apenas os dados estritamente necessários para a autenticação, controle de franquia de processamento e comunicação transacional referente ao InfoForge.
              </p>
              <h4 className="text-white font-bold text-sm">2. Dados Coletados na Waitlist</h4>
              <p>
                Ao preencher o formulário de lista de espera, coletamos nome, endereço de e-mail e perfil profissional com a finalidade exclusiva de análise de elegibilidade para os lotes do Private Beta e envio de convite.
              </p>
              <h4 className="text-white font-bold text-sm">3. Não Treinamento com Dados do Usuário</h4>
              <p>
                Os materiais, teses, prompts e rascunhos inseridos no InfoForge não são utilizados para treinamento de modelos de inteligência artificial de código aberto ou públicos. Seus dados permanecem confinados e isolados.
              </p>
              <h4 className="text-white font-bold text-sm">4. Direitos do Titular</h4>
              <p>
                Você poderá solicitar a confirmação, correção ou exclusão definitiva de seus dados a qualquer momento enviando uma solicitação para <strong className="text-white">{SITE_CONFIG.supportEmail}</strong>.
              </p>
            </>
          )}

          {type === 'ai' && (
            <>
              <h4 className="text-white font-bold text-sm">1. IA Como Assistente de Engenharia</h4>
              <p>
                O InfoForge emprega modelos generativos e determinísticos para automatizar o arranjo editorial, a síntese de mercado e a diagramação visual. A ferramenta funciona como um amplificador de produtividade intelectual, não como substituto do discernimento humano.
              </p>
              <h4 className="text-white font-bold text-sm">2. Mitigação Ativa de Alucinações</h4>
              <p>
                Nossa arquitetura conta com rotinas de fact-check cruzado e restrição contextual. Contudo, em virtude da natureza probabilística dos modelos fundacionais, o criador deve sempre auditar formulações empíricas, números e fontes citadas.
              </p>
              <h4 className="text-white font-bold text-sm">3. Diretriz Anti-Enriquecimento Fácil</h4>
              <p>
                A plataforma repudia categoricamente alegações de lucros fáceis, vendas automatizadas sem produto real ou esquemas predatórios. O sucesso de qualquer infoproduto decorre do valor didático e da relevância comercial entregue ao comprador.
              </p>
            </>
          )}
        </div>

        {/* Footer */}
        <div className="mt-6 pt-4 border-t border-brand-border flex justify-end shrink-0">
          <button
            onClick={onClose}
            className="px-6 py-2.5 rounded-xl bg-brand-surfaceLight hover:bg-white/10 text-white text-xs font-semibold transition-colors"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  );
};
